import { Dropdown as PrimeDropdown } from 'primereact/dropdown';

export type DropdownOption = {
    label: string;
    value: string | number;
    disabled?: boolean;
};

export type DropdownOptionGroup = {
    label: string;
    items: DropdownOption[];
};

type DropdownValue = string | number | null | undefined;

const isGrouped = (options: DropdownOption[] | DropdownOptionGroup[]): options is DropdownOptionGroup[] =>
    options.length > 0 && 'items' in options[0];

const flatten = (options: DropdownOption[] | DropdownOptionGroup[]): DropdownOption[] =>
    isGrouped(options) ? options.flatMap(group => group.items) : options;

function Dropdown(props: {
    value: DropdownValue;
    options: DropdownOption[] | DropdownOptionGroup[];
    onChange: (value: any) => void;
    placeholder?: string;
    disabled?: boolean;
    filter?: boolean;
    filterPlaceholder?: string;
    showClear?: boolean;
    id?: string;
    ariaLabel?: string;
    className?: string;
    invalid?: boolean;
    valid?: boolean;
    autoFocus?: boolean;
    icon?: string;
    appendTo?: 'self';
}) {
    const {
        value, options, onChange, placeholder, disabled, filter, filterPlaceholder,
        showClear, id, ariaLabel, className, invalid, valid, autoFocus, icon, appendTo
    } = props;

    const grouped = isGrouped(options);
    const matched = flatten(options).find(option => String(option.value) === String(value ?? ''));
    const resolvedValue = matched ? matched.value : null;
    const useFilter = filter ?? flatten(options).length > 10;

    const classes = ['dbm-dropdown', className, invalid ? 'is-invalid' : '', valid ? 'is-valid' : '', icon ? 'dbm-dropdown-icon' : '']
        .filter(Boolean)
        .join(' ');

    return (
        <PrimeDropdown
            inputId={id}
            value={resolvedValue}
            options={options}
            optionLabel="label"
            optionValue="value"
            optionDisabled="disabled"
            optionGroupLabel={grouped ? 'label' : undefined}
            optionGroupChildren={grouped ? 'items' : undefined}
            onChange={(e) => onChange(e.value)}
            placeholder={placeholder}
            disabled={disabled}
            filter={useFilter}
            filterPlaceholder={filterPlaceholder ?? 'Search'}
            showFilterClear
            emptyFilterMessage="No matches"
            emptyMessage="No options"
            showClear={showClear}
            scrollHeight="260px"
            className={classes}
            panelClassName="dbm-dropdown-panel"
            appendTo={appendTo}
            ariaLabel={ariaLabel}
            autoFocus={autoFocus}
            valueTemplate={icon ? () => <i className={icon} aria-hidden="true"/> : undefined}
        />
    );
}

export default Dropdown;
